<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useProjectionsStore } from '../stores/projections'
import { useTeamMatchupStatsStore } from '../stores/teamMatchupStats'

const props = defineProps({
  isOpen: Boolean,
  teamType: String,
  matchupProjections: Object,
  matchupLoading: Boolean,
  matchupError: String
})

const emit = defineEmits(['close', 'reload'])

const projectionsStore = useProjectionsStore()
const matchupStore = useTeamMatchupStatsStore()

// Modal tabs
const activeTab = ref('projections') // projections, debug, mapping

// Selected stat for detailed view
const selectedStat = ref('ppg')

// Mapping state
const showLeagueMappingForm = ref(false)
const showTeamMappingForm = ref(false)
const selectedStatsLeague = ref('')
const selectedStatsTeam = ref('')
const mappingTeamContext = ref('') // 'home' or 'away'

// Search filters
const leagueSearchQuery = ref('')
const teamSearchQuery = ref('')

// Filtered leagues for search
const filteredLeagues = computed(() => {
  if (!leagueSearchQuery.value) return matchupStore.availableLeagues
  const query = leagueSearchQuery.value.toLowerCase()
  return matchupStore.availableLeagues.filter(l => 
    l.name.toLowerCase().includes(query)
  )
})

// Filtered teams for search
const filteredTeams = computed(() => {
  if (!teamSearchQuery.value) return matchupStore.teamNames
  const query = teamSearchQuery.value.toLowerCase()
  return matchupStore.teamNames.filter(t => 
    t.toLowerCase().includes(query)
  )
})

// Stat labels
const statLabels = {
  ppg: 'Points (PPG)',
  tpm: '3-Pointers (3PM)',
  rpg: 'Rebounds (RPG)',
  apg: 'Assists (APG)',
  spg: 'Steals (SPG)',
  bpg: 'Blocks (BPG)',
  tov: 'Turnovers (TOV)'
}

const statOrder = ['ppg', 'tpm', 'rpg', 'apg', 'spg', 'bpg', 'tov']

// Computed properties - include both names and IDs for proper mapping
const homeTeam = computed(() => projectionsStore.projectionsData?.home_team?.team_name)
const awayTeam = computed(() => projectionsStore.projectionsData?.away_team?.team_name)
const leagueName = computed(() => projectionsStore.projectionsData?.league?.league_name)

// OddsComposer IDs for persistent mapping
const ocLeagueId = computed(() => projectionsStore.projectionsData?.league?.id)
const ocHomeTeamId = computed(() => projectionsStore.projectionsData?.home_team?.id)
const ocAwayTeamId = computed(() => projectionsStore.projectionsData?.away_team?.id)

// Handle both naming conventions: 'home'/'away' and 'A'/'B'
const isHomeTeam = computed(() => props.teamType === 'home' || props.teamType === 'A')
const currentTeam = computed(() => isHomeTeam.value ? homeTeam.value : awayTeam.value)
const opponentTeam = computed(() => isHomeTeam.value ? awayTeam.value : homeTeam.value)

// Get mapped team names (check by OC ID first, then name)
const mappedHomeTeam = computed(() => 
  matchupStore.teamMappings[ocHomeTeamId.value] || matchupStore.teamMappings[homeTeam.value] || homeTeam.value
)
const mappedAwayTeam = computed(() => 
  matchupStore.teamMappings[ocAwayTeamId.value] || matchupStore.teamMappings[awayTeam.value] || awayTeam.value
)

const hasLeagueMapping = computed(() => !!matchupStore.leagueMapping)
const hasTeamMappings = computed(() => {
  const hasHome = matchupStore.teamMappings[ocHomeTeamId.value] || matchupStore.teamMappings[homeTeam.value]
  const hasAway = matchupStore.teamMappings[ocAwayTeamId.value] || matchupStore.teamMappings[awayTeam.value]
  return hasHome && hasAway
})

// Both matchup projections
const bothProjections = computed(() => {
  if (!matchupStore.hasData) return null
  return matchupStore.calculateBothMatchups(mappedHomeTeam.value, mappedAwayTeam.value)
})

// Methods
function closeModal() {
  emit('close')
}

async function reloadData() {
  // Clear cache before reload
  matchupStore.resetCache()
  
  // If we have a league mapping, reload the stats
  if (matchupStore.leagueMapping) {
    await matchupStore.fetchLeagueStats(matchupStore.leagueMapping.stats_league_id)
  }
  
  emit('reload')
}

function selectStat(stat) {
  selectedStat.value = stat
}

// League mapping
async function openLeagueMappingForm() {
  showLeagueMappingForm.value = true
  leagueSearchQuery.value = ''
  selectedStatsLeague.value = ''
  // Always fetch fresh leagues
  await matchupStore.fetchLeagues()
}

async function saveLeagueMapping() {
  if (!selectedStatsLeague.value) return
  
  // Save with OC league ID for persistent mapping
  await matchupStore.saveLeagueMapping(leagueName.value, selectedStatsLeague.value, ocLeagueId.value)
  showLeagueMappingForm.value = false
  
  // After saving league mapping, automatically open team mapping form
  // so user can continue mapping teams
  if (!hasTeamMappings.value) {
    await openTeamMappingForm('home')
  }
}

// Team mapping
async function openTeamMappingForm(context) {
  mappingTeamContext.value = context
  showTeamMappingForm.value = true
  teamSearchQuery.value = ''
  selectedStatsTeam.value = ''
  
  // Ensure league stats are loaded (for team list)
  if (!matchupStore.hasData && matchupStore.leagueMapping) {
    await matchupStore.fetchLeagueStats(matchupStore.leagueMapping.stats_league_id)
  }
}

async function saveTeamMapping() {
  if (!selectedStatsTeam.value) return
  
  const isHome = mappingTeamContext.value === 'home'
  const sourceTeam = isHome ? homeTeam.value : awayTeam.value
  const sourceTeamId = isHome ? ocHomeTeamId.value : ocAwayTeamId.value
  
  // Save with OC IDs for persistent mapping
  await matchupStore.saveTeamMapping(
    sourceTeam, 
    selectedStatsTeam.value, 
    sourceTeamId,
    ocLeagueId.value,
    matchupStore.leagueMapping?.stats_league_id
  )
  showTeamMappingForm.value = false
  
  // If we just mapped the home team and away team isn't mapped, open away team form
  if (isHome && !matchupStore.teamMappings[awayTeam.value] && !matchupStore.teamMappings[ocAwayTeamId.value]) {
    await openTeamMappingForm('away')
  } 
  // If both teams are now mapped, reload data to show projections
  else if ((matchupStore.teamMappings[homeTeam.value] || matchupStore.teamMappings[ocHomeTeamId.value]) && 
           (matchupStore.teamMappings[awayTeam.value] || matchupStore.teamMappings[ocAwayTeamId.value])) {
    await reloadData()
    activeTab.value = 'projections'
  }
}

// Format percentage
function formatPercent(value) {
  if (!value && value !== 0) return '-'
  const formatted = value.toFixed(1)
  return value > 0 ? `+${formatted}%` : `${formatted}%`
}

// Get color class based on value
function getColorClass(value) {
  if (!value) return 'text-gray-500'
  if (value > 5) return 'text-green-600 font-semibold'
  if (value > 0) return 'text-green-500'
  if (value < -5) return 'text-red-600 font-semibold'
  if (value < 0) return 'text-red-500'
  return 'text-gray-500'
}

onMounted(async () => {
  // Fetch leagues list once when modal opens
  if (!matchupStore.availableLeagues.length) {
    await matchupStore.fetchLeagues()
  }
  
  // If league is already mapped, ensure stats are loaded (for team list)
  if (matchupStore.leagueMapping && !matchupStore.hasData) {
    await matchupStore.fetchLeagueStats(matchupStore.leagueMapping.stats_league_id)
  }
  
  // Auto-fetch team mappings from database if league is mapped (using OC IDs)
  if (matchupStore.leagueMapping && homeTeam.value && awayTeam.value) {
    const statsLeagueId = matchupStore.leagueMapping?.stats_league_id
    
    // Check for existing team mappings in database (by OC ID first, then name)
    if (matchupStore.teamMappings[ocHomeTeamId.value] === undefined && matchupStore.teamMappings[homeTeam.value] === undefined) {
      await matchupStore.checkTeamMapping(homeTeam.value, ocHomeTeamId.value, ocLeagueId.value, statsLeagueId)
    }
    if (matchupStore.teamMappings[ocAwayTeamId.value] === undefined && matchupStore.teamMappings[awayTeam.value] === undefined) {
      await matchupStore.checkTeamMapping(awayTeam.value, ocAwayTeamId.value, ocLeagueId.value, statsLeagueId)
    }
  }
  
  // Determine what to show based on mapping status
  if (!hasLeagueMapping.value) {
    // No league mapping - show league form
    activeTab.value = 'mapping'
    showLeagueMappingForm.value = true
  }
  else if (!hasTeamMappings.value) {
    // League mapped but teams aren't - show team mapping form
    activeTab.value = 'mapping'
    openTeamMappingForm('home')
  }
  // else: All mapped - stay on projections tab (default)
})
</script>

<template>
  <Teleport to="body">
    <div 
      v-if="isOpen" 
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      @click.self="closeModal"
    >
      <div class="bg-white rounded-xl shadow-2xl w-[900px] max-h-[85vh] overflow-hidden flex flex-col">
        <!-- Header -->
        <div class="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 class="text-xl font-bold text-white flex items-center gap-2">
              <i class="fas fa-calculator"></i>
              Team Matchup Calculator
            </h2>
            <p class="text-purple-200 text-sm mt-1">
              {{ currentTeam }} vs {{ opponentTeam }} ({{ isHomeTeam ? 'Home' : 'Away' }} Offense)
            </p>
          </div>
          <button 
            @click="closeModal"
            class="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-2 transition-all"
          >
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>

        <!-- Tabs -->
        <div class="border-b border-gray-200 px-6">
          <nav class="flex gap-4">
            <button 
              v-for="tab in ['projections', 'debug', 'mapping']"
              :key="tab"
              @click="activeTab = tab"
              class="py-3 px-4 text-sm font-medium border-b-2 transition-all"
              :class="activeTab === tab 
                ? 'border-purple-500 text-purple-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'"
            >
              {{ tab.charAt(0).toUpperCase() + tab.slice(1) }}
            </button>
          </nav>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto p-6">
          <!-- Loading State -->
          <div v-if="matchupLoading" class="flex items-center justify-center h-64">
            <div class="text-center">
              <i class="fas fa-spinner fa-spin text-4xl text-purple-500 mb-4"></i>
              <p class="text-gray-500">Loading matchup data...</p>
            </div>
          </div>

          <!-- Team mapping required - only show when NOT on mapping tab -->
          <div v-else-if="matchupError === 'Team mapping required' && activeTab !== 'mapping'" class="bg-orange-50 border border-orange-200 rounded-lg p-6 text-center">
            <i class="fas fa-users text-4xl text-orange-400 mb-4"></i>
            <p class="text-orange-600 font-medium mb-2">Team mapping required</p>
            <p class="text-orange-500 text-sm mb-4">Map "{{ homeTeam }}" and "{{ awayTeam }}" to stats teams</p>
            <button 
              @click="activeTab = 'mapping'; openTeamMappingForm('home')"
              class="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all"
            >
              <i class="fas fa-cog mr-2"></i>
              Configure Team Mapping
            </button>
          </div>

          <!-- Error State - only show for actual errors, not mapping issues, and not when on mapping tab -->
          <div v-else-if="matchupError && matchupError !== 'League mapping not configured' && matchupError !== 'Team mapping required' && activeTab !== 'mapping'" class="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <i class="fas fa-exclamation-triangle text-4xl text-red-400 mb-4"></i>
            <p class="text-red-600 font-medium">{{ matchupError }}</p>
            <button 
              @click="activeTab = 'mapping'"
              class="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all"
            >
              Configure Mapping
            </button>
          </div>

          <!-- No mapping configured - show mapping form directly -->
          <div v-else-if="matchupError === 'League mapping not configured' && activeTab !== 'mapping'" class="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <i class="fas fa-link text-4xl text-yellow-500 mb-4"></i>
            <p class="text-yellow-700 font-medium mb-2">League mapping not configured</p>
            <p class="text-yellow-600 text-sm mb-4">Map "{{ leagueName }}" to a stats league to enable projections</p>
            <button 
              @click="activeTab = 'mapping'; openLeagueMappingForm()"
              class="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all"
            >
              <i class="fas fa-cog mr-2"></i>
              Configure Mapping
            </button>
          </div>

          <!-- Projections Tab -->
          <div v-else-if="activeTab === 'projections'">
            <!-- Both Matchups Grid -->
            <div class="grid grid-cols-2 gap-6">
              <!-- Home Team Projections -->
              <div class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
                <h3 class="font-bold text-blue-800 mb-3 flex items-center gap-2">
                  <i class="fas fa-home"></i>
                  {{ homeTeam }} (Home)
                  <span class="text-xs text-blue-600">attacking {{ awayTeam }}'s defense</span>
                </h3>
                <div class="space-y-2">
                  <div 
                    v-for="stat in statOrder" 
                    :key="stat"
                    class="flex items-center justify-between bg-white rounded-lg px-3 py-2 hover:shadow transition-shadow cursor-pointer"
                    @click="selectStat(stat); activeTab = 'debug'"
                  >
                    <span class="text-sm font-medium text-gray-700">{{ statLabels[stat] }}</span>
                    <div class="flex items-center gap-3">
                      <span class="font-mono text-lg font-bold text-blue-700">
                        {{ bothProjections?.teamA?.projections?.[stat]?.projected?.toFixed(1) || '-' }}
                      </span>
                      <span 
                        class="text-xs font-mono"
                        :class="getColorClass(bothProjections?.teamA?.projections?.[stat]?.muPercent)"
                      >
                        {{ formatPercent(bothProjections?.teamA?.projections?.[stat]?.muPercent) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Away Team Projections -->
              <div class="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4">
                <h3 class="font-bold text-orange-800 mb-3 flex items-center gap-2">
                  <i class="fas fa-plane"></i>
                  {{ awayTeam }} (Away)
                  <span class="text-xs text-orange-600">attacking {{ homeTeam }}'s defense</span>
                </h3>
                <div class="space-y-2">
                  <div 
                    v-for="stat in statOrder" 
                    :key="stat"
                    class="flex items-center justify-between bg-white rounded-lg px-3 py-2 hover:shadow transition-shadow cursor-pointer"
                    @click="selectStat(stat); activeTab = 'debug'"
                  >
                    <span class="text-sm font-medium text-gray-700">{{ statLabels[stat] }}</span>
                    <div class="flex items-center gap-3">
                      <span class="font-mono text-lg font-bold text-orange-700">
                        {{ bothProjections?.teamB?.projections?.[stat]?.projected?.toFixed(1) || '-' }}
                      </span>
                      <span 
                        class="text-xs font-mono"
                        :class="getColorClass(bothProjections?.teamB?.projections?.[stat]?.muPercent)"
                      >
                        {{ formatPercent(bothProjections?.teamB?.projections?.[stat]?.muPercent) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Combined Totals -->
            <div class="mt-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4">
              <h3 class="font-bold text-purple-800 mb-3">
                <i class="fas fa-chart-bar mr-2"></i>
                Combined Game Totals
              </h3>
              <div class="grid grid-cols-7 gap-4">
                <div 
                  v-for="stat in statOrder" 
                  :key="stat"
                  class="text-center bg-white rounded-lg p-3"
                >
                  <div class="text-xs text-gray-500 mb-1">{{ statLabels[stat].split(' ')[0] }}</div>
                  <div class="text-xl font-bold text-purple-700">
                    {{ 
                      ((bothProjections?.teamA?.projections?.[stat]?.projected || 0) + 
                       (bothProjections?.teamB?.projections?.[stat]?.projected || 0)).toFixed(1)
                    }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Debug Tab -->
          <div v-else-if="activeTab === 'debug'">
            <!-- Stat Selector -->
            <div class="flex gap-2 mb-6 flex-wrap">
              <button
                v-for="stat in statOrder"
                :key="stat"
                @click="selectStat(stat)"
                class="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                :class="selectedStat === stat 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              >
                {{ statLabels[stat].split(' ')[0] }}
              </button>
            </div>

            <!-- Calculation Breakdown -->
            <div class="grid grid-cols-2 gap-6">
              <!-- Home Team Calculation -->
              <div class="bg-blue-50 rounded-xl p-4">
                <h4 class="font-bold text-blue-800 mb-4">{{ homeTeam }} → {{ statLabels[selectedStat] }}</h4>
                <div class="space-y-3 text-sm">
                  <div class="flex justify-between">
                    <span class="text-gray-600">Team Avg (Offense):</span>
                    <span class="font-mono font-bold">{{ bothProjections?.teamA?.projections?.[selectedStat]?.homeStat?.toFixed(2) }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-600">Opponent Def Avg:</span>
                    <span class="font-mono font-bold">{{ bothProjections?.teamA?.projections?.[selectedStat]?.oppStat?.toFixed(2) }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-600">League Avg:</span>
                    <span class="font-mono">{{ bothProjections?.teamA?.projections?.[selectedStat]?.leagueAvg?.toFixed(2) }}</span>
                  </div>
                  <hr class="border-blue-200">
                  <div class="flex justify-between">
                    <span class="text-gray-600">MU vs League Avg:</span>
                    <span class="font-mono" :class="getColorClass(bothProjections?.teamA?.projections?.[selectedStat]?.muVsAvg)">
                      {{ bothProjections?.teamA?.projections?.[selectedStat]?.muVsAvg?.toFixed(2) }}
                    </span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-600">MU Value:</span>
                    <span class="font-mono" :class="getColorClass(bothProjections?.teamA?.projections?.[selectedStat]?.muPercent)">
                      {{ formatPercent(bothProjections?.teamA?.projections?.[selectedStat]?.muPercent) }}
                    </span>
                  </div>
                  <hr class="border-blue-200">
                  <div class="flex justify-between bg-blue-100 p-2 rounded">
                    <span class="font-bold text-blue-800">Projected:</span>
                    <span class="font-mono font-bold text-blue-700 text-lg">
                      {{ bothProjections?.teamA?.projections?.[selectedStat]?.projected?.toFixed(2) }}
                    </span>
                  </div>
                </div>
                
                <!-- Formula -->
                <div class="mt-4 p-3 bg-white rounded-lg text-xs font-mono text-gray-600">
                  <div class="mb-1 font-semibold">Formula:</div>
                  <div>MU vs Avg = OppDef - LeagueAvg</div>
                  <div>MU Value = MU vs Avg / LeagueAvg</div>
                  <div>Projected = (TeamAvg × MU Value) + TeamAvg</div>
                </div>
              </div>

              <!-- Away Team Calculation -->
              <div class="bg-orange-50 rounded-xl p-4">
                <h4 class="font-bold text-orange-800 mb-4">{{ awayTeam }} → {{ statLabels[selectedStat] }}</h4>
                <div class="space-y-3 text-sm">
                  <div class="flex justify-between">
                    <span class="text-gray-600">Team Avg (Offense):</span>
                    <span class="font-mono font-bold">{{ bothProjections?.teamB?.projections?.[selectedStat]?.homeStat?.toFixed(2) }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-600">Opponent Def Avg:</span>
                    <span class="font-mono font-bold">{{ bothProjections?.teamB?.projections?.[selectedStat]?.oppStat?.toFixed(2) }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-600">League Avg:</span>
                    <span class="font-mono">{{ bothProjections?.teamB?.projections?.[selectedStat]?.leagueAvg?.toFixed(2) }}</span>
                  </div>
                  <hr class="border-orange-200">
                  <div class="flex justify-between">
                    <span class="text-gray-600">MU vs League Avg:</span>
                    <span class="font-mono" :class="getColorClass(bothProjections?.teamB?.projections?.[selectedStat]?.muVsAvg)">
                      {{ bothProjections?.teamB?.projections?.[selectedStat]?.muVsAvg?.toFixed(2) }}
                    </span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-600">MU Value:</span>
                    <span class="font-mono" :class="getColorClass(bothProjections?.teamB?.projections?.[selectedStat]?.muPercent)">
                      {{ formatPercent(bothProjections?.teamB?.projections?.[selectedStat]?.muPercent) }}
                    </span>
                  </div>
                  <hr class="border-orange-200">
                  <div class="flex justify-between bg-orange-100 p-2 rounded">
                    <span class="font-bold text-orange-800">Projected:</span>
                    <span class="font-mono font-bold text-orange-700 text-lg">
                      {{ bothProjections?.teamB?.projections?.[selectedStat]?.projected?.toFixed(2) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- League Averages Reference -->
            <div class="mt-6 bg-gray-50 rounded-xl p-4">
              <h4 class="font-bold text-gray-700 mb-3">
                <i class="fas fa-chart-line mr-2"></i>
                League Averages Reference
              </h4>
              <div class="grid grid-cols-7 gap-4 text-center">
                <div v-for="stat in statOrder" :key="stat" class="bg-white rounded-lg p-2">
                  <div class="text-xs text-gray-500">{{ stat.toUpperCase() }}</div>
                  <div class="font-mono font-bold">{{ matchupStore.leagueAverages[stat]?.toFixed(1) || '-' }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Mapping Tab -->
          <div v-else-if="activeTab === 'mapping'">
            <!-- League Mapping -->
            <div class="bg-gray-50 rounded-xl p-4 mb-6">
              <h4 class="font-bold text-gray-700 mb-4 flex items-center gap-2">
                <i class="fas fa-link"></i>
                League Mapping
                <span v-if="hasLeagueMapping" class="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                  ✓ Configured
                </span>
                <span v-else class="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                  Not configured
                </span>
              </h4>
              
              <div class="grid grid-cols-[1fr_auto_1fr_auto] gap-4 items-end">
                <!-- Source League -->
                <div>
                  <label class="block text-sm text-gray-600 mb-1">Source League (from fixture)</label>
                  <div class="bg-white border-2 border-blue-200 rounded-lg px-4 py-2 font-medium text-blue-700">
                    {{ leagueName || 'N/A' }}
                  </div>
                </div>
                
                <div class="text-2xl text-gray-400 pb-2">→</div>
                
                <!-- Stats League Selection -->
                <div>
                  <label class="block text-sm text-gray-600 mb-1">Stats League (from database)</label>
                  <div v-if="!showLeagueMappingForm && hasLeagueMapping" class="bg-white border-2 border-green-200 rounded-lg px-4 py-2">
                    <span class="font-medium text-green-600">
                      <i class="fas fa-check-circle mr-1"></i>
                      {{ matchupStore.availableLeagues.find(l => l.league_id === matchupStore.leagueMapping?.stats_league_id)?.name || matchupStore.leagueMapping?.stats_league_id }}
                    </span>
                  </div>
                  <div v-else class="space-y-2">
                    <!-- Loading state -->
                    <div v-if="matchupStore.loading" class="text-center py-4">
                      <i class="fas fa-spinner fa-spin text-purple-500"></i>
                      <span class="ml-2 text-gray-500">Loading leagues...</span>
                    </div>
                    <template v-else>
                      <!-- Search Input -->
                      <input 
                        v-model="leagueSearchQuery"
                        type="text"
                        placeholder="🔍 Search leagues by name..."
                        class="w-full border rounded-lg px-3 py-2 text-sm"
                      />
                      <!-- League List (clickable) -->
                      <div class="border rounded-lg max-h-48 overflow-y-auto bg-white">
                        <div v-if="filteredLeagues.length === 0" class="p-3 text-gray-500 text-center text-sm">
                          No leagues found. Make sure leagues are scraped first.
                        </div>
                        <div 
                          v-for="league in filteredLeagues" 
                          :key="league.league_id"
                          @click="selectedStatsLeague = league.league_id"
                          class="px-3 py-2 hover:bg-purple-50 cursor-pointer flex items-center justify-between border-b last:border-b-0"
                          :class="selectedStatsLeague === league.league_id ? 'bg-purple-100 text-purple-700' : ''"
                        >
                          <span>{{ league.name }}</span>
                          <i v-if="selectedStatsLeague === league.league_id" class="fas fa-check text-purple-600"></i>
                        </div>
                      </div>
                      <div class="text-xs text-gray-500">
                        {{ filteredLeagues.length }} leagues in database
                        <span v-if="selectedStatsLeague" class="text-purple-600 ml-2">
                          • Selected: {{ filteredLeagues.find(l => l.league_id === selectedStatsLeague)?.name }}
                        </span>
                      </div>
                    </template>
                  </div>
                </div>
                
                <!-- Actions -->
                <div class="pb-2">
                  <template v-if="!showLeagueMappingForm && hasLeagueMapping">
                    <button 
                      @click="openLeagueMappingForm"
                      class="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all"
                    >
                      Change
                    </button>
                  </template>
                  <template v-else>
                    <div class="flex flex-col gap-2">
                      <button 
                        @click="saveLeagueMapping"
                        :disabled="!selectedStatsLeague"
                        class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all disabled:opacity-50"
                      >
                        <i class="fas fa-save mr-1"></i>
                        Save
                      </button>
                      <button 
                        v-if="hasLeagueMapping"
                        @click="showLeagueMappingForm = false"
                        class="px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-all text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <!-- Team Mappings -->
            <div class="bg-gray-50 rounded-xl p-4">
              <h4 class="font-bold text-gray-700 mb-4 flex items-center gap-2">
                <i class="fas fa-users"></i>
                Team Mappings
                <span v-if="!hasLeagueMapping" class="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                  Configure league first
                </span>
                <span v-else-if="matchupStore.loading" class="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                  <i class="fas fa-spinner fa-spin mr-1"></i> Loading teams...
                </span>
                <span v-else-if="matchupStore.teamNames.length" class="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full">
                  {{ matchupStore.teamNames.length }} teams available
                </span>
              </h4>
              
              <!-- Team search when mapping -->
              <div v-if="showTeamMappingForm" class="mb-4 p-3 bg-white rounded-lg border">
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Search teams in {{ matchupStore.availableLeagues.find(l => l.league_id === matchupStore.currentLeagueId)?.name }}:
                </label>
                <input 
                  v-model="teamSearchQuery"
                  type="text"
                  placeholder="🔍 Search teams..."
                  class="w-full border rounded-lg px-3 py-2 text-sm mb-2"
                />
                
                <!-- Loading indicator -->
                <div v-if="matchupStore.loading" class="text-center py-4 text-gray-500">
                  <i class="fas fa-spinner fa-spin mr-2"></i>Loading teams...
                </div>
                
                <!-- No teams message -->
                <div v-else-if="!matchupStore.teamNames.length" class="text-center py-4 text-orange-500">
                  <i class="fas fa-exclamation-triangle mr-2"></i>No teams loaded. Try clicking "Reload Data".
                </div>
                
                <div v-else class="max-h-40 overflow-y-auto border rounded-lg">
                  <div 
                    v-for="team in filteredTeams" 
                    :key="team"
                    @click="selectedStatsTeam = team"
                    class="px-3 py-2 hover:bg-purple-50 cursor-pointer flex items-center justify-between"
                    :class="selectedStatsTeam === team ? 'bg-purple-100 text-purple-700' : ''"
                  >
                    <span>{{ team }}</span>
                    <i v-if="selectedStatsTeam === team" class="fas fa-check text-purple-600"></i>
                  </div>
                </div>
                <div class="text-xs text-gray-500 mt-1">{{ filteredTeams.length }} teams found</div>
              </div>
              
              <!-- Home Team -->
              <div class="flex items-center gap-4 mb-4">
                <div class="flex-1">
                  <label class="block text-sm text-gray-600 mb-1">Home Team (Source)</label>
                  <div class="bg-white border-2 border-blue-200 rounded-lg px-4 py-2 font-medium text-blue-700">
                    {{ homeTeam || 'N/A' }}
                  </div>
                </div>
                <div class="text-2xl text-gray-400">→</div>
                <div class="flex-1">
                  <label class="block text-sm text-gray-600 mb-1">Stats Team</label>
                  <div v-if="showTeamMappingForm && mappingTeamContext === 'home'" class="bg-purple-50 border-2 border-purple-300 rounded-lg px-4 py-2">
                    <span v-if="selectedStatsTeam" class="font-medium text-purple-700">
                      {{ selectedStatsTeam }}
                    </span>
                    <span v-else class="text-gray-400 italic">Select from list above...</span>
                  </div>
                  <div v-else class="bg-white border rounded-lg px-4 py-2">
                    <template v-if="matchupStore.teamMappings[homeTeam]">
                      <span class="font-medium text-green-600">
                        <i class="fas fa-check-circle mr-1"></i>
                        {{ matchupStore.teamMappings[homeTeam] }}
                      </span>
                    </template>
                    <template v-else>
                      <span class="text-orange-500"><i class="fas fa-exclamation-circle mr-1"></i>Not mapped</span>
                    </template>
                  </div>
                </div>
                <div>
                  <template v-if="!showTeamMappingForm || mappingTeamContext !== 'home'">
                    <button 
                      @click="openTeamMappingForm('home')"
                      :disabled="!hasLeagueMapping"
                      class="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {{ matchupStore.teamMappings[homeTeam] ? 'Change' : 'Map' }}
                    </button>
                  </template>
                  <template v-else>
                    <div class="flex gap-2">
                      <button 
                        @click="saveTeamMapping"
                        :disabled="!selectedStatsTeam"
                        class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all disabled:opacity-50"
                      >
                        Save
                      </button>
                      <button 
                        @click="showTeamMappingForm = false; teamSearchQuery = ''"
                        class="px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </template>
                </div>
              </div>

              <!-- Away Team -->
              <div class="flex items-center gap-4">
                <div class="flex-1">
                  <label class="block text-sm text-gray-600 mb-1">Away Team (Source)</label>
                  <div class="bg-white border-2 border-orange-200 rounded-lg px-4 py-2 font-medium text-orange-700">
                    {{ awayTeam || 'N/A' }}
                  </div>
                </div>
                <div class="text-2xl text-gray-400">→</div>
                <div class="flex-1">
                  <label class="block text-sm text-gray-600 mb-1">Stats Team</label>
                  <div v-if="showTeamMappingForm && mappingTeamContext === 'away'" class="bg-purple-50 border-2 border-purple-300 rounded-lg px-4 py-2">
                    <span v-if="selectedStatsTeam" class="font-medium text-purple-700">
                      {{ selectedStatsTeam }}
                    </span>
                    <span v-else class="text-gray-400 italic">Select from list above...</span>
                  </div>
                  <div v-else class="bg-white border rounded-lg px-4 py-2">
                    <template v-if="matchupStore.teamMappings[awayTeam]">
                      <span class="font-medium text-green-600">
                        <i class="fas fa-check-circle mr-1"></i>
                        {{ matchupStore.teamMappings[awayTeam] }}
                      </span>
                    </template>
                    <template v-else>
                      <span class="text-orange-500"><i class="fas fa-exclamation-circle mr-1"></i>Not mapped</span>
                    </template>
                  </div>
                </div>
                <div>
                  <template v-if="!showTeamMappingForm || mappingTeamContext !== 'away'">
                    <button 
                      @click="openTeamMappingForm('away')"
                      :disabled="!hasLeagueMapping"
                      class="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all disabled:opacity-50"
                    >
                      {{ matchupStore.teamMappings[awayTeam] ? 'Change' : 'Map' }}
                    </button>
                  </template>
                  <template v-else>
                    <div class="flex gap-2">
                      <button 
                        @click="saveTeamMapping"
                        :disabled="!selectedStatsTeam"
                        class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all disabled:opacity-50"
                      >
                        Save
                      </button>
                      <button 
                        @click="showTeamMappingForm = false; teamSearchQuery = ''"
                        class="px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </template>
                </div>
              </div>

              <div v-if="!hasLeagueMapping" class="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                <i class="fas fa-info-circle mr-1"></i>
                Configure league mapping first before mapping teams.
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
          <div class="text-sm text-gray-500">
            <template v-if="matchupStore.currentLeagueId">
              League: {{ matchupStore.availableLeagues.find(l => l.league_id === matchupStore.currentLeagueId)?.name }}
              <span class="mx-2">•</span>
              {{ matchupStore.teamStats.length }} teams loaded
            </template>
          </div>
          <div class="flex gap-3">
            <button 
              @click="reloadData"
              class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-all"
            >
              <i class="fas fa-sync-alt mr-2"></i>
              Reload Data
            </button>
            <button 
              @click="closeModal"
              class="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
